import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import 'dayjs/locale/pt-br.js';
import { entityService } from '../services/entityService.js';
import { agendaService } from '../services/agendaService.js';
import { medicalRecordService } from '../services/medicalRecordService.js';
import { servicesService } from '../services/servicesService.js';
import { formatPhone, getSpeciesLabel, SPECIES_LABEL } from '../utils/formatters.js';
import placeholderImage from '../images/placeholder.png';
import ConfirmModal from './ConfirmModal.js';
import ScheduleSettings from './ScheduleSettings.js';
import './Dashboard.css';
import './MedicalRecord.css';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.locale('pt-br');


// ── Constants & Helpers ───────────────────────────────────────────────────────
const TZ = 'America/Sao_Paulo';

const STATUS_CONFIG = {
  scheduled:   { label: 'Agendado',         bg: '#EFF6FF',                     color: '#2563EB' },
  pending:     { label: 'Pendente',         bg: '#FEF9C3',                     color: '#B45309' },
  confirmed:   { label: 'Confirmado',        bg: '#DCFCE7',                     color: '#16A34A' },
  in_progress: { label: 'Em atendimento',   bg: '#FEF3C7',                     color: '#D97706' },
  completed:   { label: 'Finalizado',        bg: 'rgba(113,101,255,0.1)',       color: '#7165FF' },
  canceled:   { label: 'Cancelado',         bg: '#FEE2E2',                     color: '#DC2626' },
  rejected:    { label: 'Rejeitado',         bg: '#FEE2E2',                     color: '#DC2626' },
  no_show:     { label: 'Não compareceu',   bg: '#F3F4F6',                     color: '#6B7280' },
  blocked:     { label: 'Bloqueado',         bg: '#F3F4F6',                     color: '#374151' },
};

const VIEW_MODES = [
  { value: 'day',   label: 'Dia' },
  { value: 'week',  label: 'Semana' },
  { value: 'month', label: 'Mês' },
];

const DURATION_OPTIONS = [15, 20, 30, 45, 60, 90, 120];
const RETURN_DAYS_OPTIONS = [
  { value: '', label: 'Sem retorno definido' },
  { value: '30', label: '30 dias' },
  { value: '60', label: '60 dias' },
  { value: '90', label: '90 dias' },
];

function fmtTime(dt) { return dayjs.utc(dt).format('HH:mm'); }
function fmtDateTime(dt) { return dayjs.utc(dt).format('DD/MM/YYYY [às] HH:mm'); }

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function StatusBadge({ status, small }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: '#F3F4F6', color: '#374151' };
  return (
    <span style={{
      fontSize: small ? '11px' : '12px',
      fontWeight: '700',
      background: cfg.bg,
      color: cfg.color,
      padding: small ? '2px 8px' : '4px 10px',
      borderRadius: '20px',
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

// ── New Appointment Modal ─────────────────────────────────────────────────────
function NewAppointmentModal({ isOpen, onClose, entityId, entity, defaultDate, onSaved }) {
  const [ownerSearch, setOwnerSearch] = useState('');
  const [ownerResults, setOwnerResults] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [pets, setPets] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    petId: '', serviceId: '',
    date: '', time: '09:00',
    durationMinutes: 30, notes: '', sendReminder: true,
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isOpen) return;
    setOwnerSearch('');
    setOwnerResults([]);
    setSelectedOwner(null);
    setPets([]);
    setForm({
      petId: '', serviceId: '',
      date: defaultDate || dayjs().format('YYYY-MM-DD'),
      time: '09:00', durationMinutes: 30, notes: '', sendReminder: true,
    });
    servicesService.getServices().then(r => {
      if (r.success) setServices(r.services.filter(s => s.active));
    });
  }, [isOpen, defaultDate]);

  useEffect(() => {
    if (!ownerSearch || ownerSearch.length < 2) { setOwnerResults([]); return; }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      const r = await medicalRecordService.getOwners({ entityId, search: ownerSearch, limit: 8 });
      setOwnerResults(r.success ? (r.owners || []) : []);
      setSearchLoading(false);
    }, 350);
    return () => clearTimeout(timer);
  }, [ownerSearch, entityId]);

  useEffect(() => {
    if (!selectedOwner) { setPets([]); setForm(f => ({ ...f, petId: '' })); return; }
    medicalRecordService.getPets({ ownerId: selectedOwner.id, entityId }).then(r => {
      const petsArr = r.success ? (r.pets || []) : [];
      setPets(petsArr);
      if (petsArr.length === 1) setForm(f => ({ ...f, petId: String(petsArr[0].id) }));
    });
  }, [selectedOwner, entityId]);

  const resetOwner = () => {
    setSelectedOwner(null);
    setOwnerSearch('');
    setOwnerResults([]);
    setPets([]);
    setForm(f => ({ ...f, petId: '' }));
  };

  const handleSave = async () => {
    if (!selectedOwner && !form.petId) return toast.error('Informe o tutor ou selecione um pet');
    if (!form.date || !form.time) return toast.error('Informe data e hora');
    const dateTime = dayjs(`${form.date}T${form.time}`);
    if (dateTime.isBefore(dayjs())) return toast.error('Não é possível agendar em data/hora passada');
    setSaving(true);
    const result = await agendaService.createAgenda({
      entityId,
      petId: form.petId ? Number(form.petId) : undefined,
      ownerId: selectedOwner?.id || undefined,
      serviceId: form.serviceId ? Number(form.serviceId) : undefined,
      dateTime: dateTime.format('YYYY-MM-DDTHH:mm:ss'),
      durationMinutes: Number(form.durationMinutes) || 30,
      notes: form.notes || undefined,
      sendReminder: form.sendReminder,
    });
    setSaving(false);
    if (result.success) {
      toast.success('Agendamento criado!');
      onSaved && onSaved();
      onClose();
    } else {
      toast.error(result.error || 'Erro ao criar agendamento');
    }
  };

  if (!isOpen) return null;

  const inputSt = {
    width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb',
    borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box',
    outline: 'none', fontFamily: 'inherit', background: '#fff',
  };
  const labelSt = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111' }}>Novo Agendamento</h2>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#9ca3af' }}>Preencha os dados do atendimento</p>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f3f4f6', cursor: 'pointer', width: '32px', height: '32px', borderRadius: '50%', fontSize: '18px', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto', flex: 1 }}>

          {/* Tutor */}
          <div>
            <label style={labelSt}>Tutor</label>
            <input type="text" placeholder="Buscar pelo nome do tutor..." value={ownerSearch}
              onChange={e => { setOwnerSearch(e.target.value); if (selectedOwner) resetOwner(); }}
              style={inputSt} />
            {searchLoading && <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0' }}>Buscando...</p>}
            {ownerResults.length > 0 && !selectedOwner && (
              <div style={{ border: '1.5px solid #e5e7eb', borderRadius: '10px', marginTop: '4px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                {ownerResults.map(o => (
                  <div key={o.id} onClick={() => { setSelectedOwner(o); setOwnerSearch(o.name); setOwnerResults([]); }}
                    style={{ padding: '10px 14px', cursor: 'pointer', fontSize: '14px', color: '#374151', borderBottom: '1px solid #f3f4f6', background: '#fff' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <strong>{o.name}</strong>
                    {o.phone && <span style={{ marginLeft: '8px', color: '#9ca3af', fontSize: '12px' }}>{formatPhone(o.phone)}</span>}
                  </div>
                ))}
              </div>
            )}
            {selectedOwner && (
              <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'rgba(113,101,255,0.08)', borderRadius: '8px', border: '1.5px solid rgba(113,101,255,0.2)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7165FF" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                <span style={{ fontSize: '13px', color: '#7165FF', fontWeight: '600' }}>{selectedOwner.name}</span>
                {selectedOwner.phone && <span style={{ fontSize: '12px', color: '#9ca3af' }}>{formatPhone(selectedOwner.phone)}</span>}
                <button onClick={resetOwner} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '18px', lineHeight: 1, marginLeft: 'auto', padding: '0 2px' }}>×</button>
              </div>
            )}
          </div>

          {/* Pet */}
          {selectedOwner && (
            <div>
              <label style={labelSt}>Pet *</label>
              {pets.length === 0
                ? <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>Nenhum pet cadastrado para este tutor.</p>
                : <select value={form.petId} onChange={e => setForm(f => ({ ...f, petId: e.target.value }))} style={inputSt}>
                    <option value="">Selecione o pet...</option>
                    {pets.map(p => {
                        const species = p.species ? ` (${SPECIES_LABEL[p.species] || p.species})` : ''
                        return <option key={p.id} value={p.id}>{p.name}{species}</option>;
                      })}
                  </select>
              }
            </div>
          )}

          {/* Service */}
          {services.length > 0 && (
            <div>
              <label style={labelSt}>Serviço</label>
              <select value={form.serviceId} onChange={e => setForm(f => ({ ...f, serviceId: e.target.value }))} style={inputSt}>
                <option value="">Selecionar serviço (opcional)...</option>
                {services.map(s => <option key={s.serviceId || s.id} value={s.serviceId || s.id}>{s.name || s.serviceName}</option>)}
              </select>
            </div>
          )}

          {/* Date & Time */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelSt}>Data *</label>
              <input type="date" value={form.date} min={dayjs().format('YYYY-MM-DD')} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} style={inputSt} />
            </div>
            <div>
              <label style={labelSt}>Hora *</label>
              <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} style={inputSt} />
            </div>
          </div>

          {/* Duration */}
          <div>
            <label style={labelSt}>Duração</label>
            <select value={form.durationMinutes} onChange={e => setForm(f => ({ ...f, durationMinutes: e.target.value }))} style={inputSt}>
              {DURATION_OPTIONS.map(m => <option key={m} value={m}>{m} minutos</option>)}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label style={labelSt}>Observações</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3}
              placeholder="Ex: Vacinação anual, trazer carteirinha..."
              style={{ ...inputSt, resize: 'vertical' }} />
          </div>

          {/* Reminder */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={form.sendReminder} onChange={e => setForm(f => ({ ...f, sendReminder: e.target.checked }))}
              style={{ width: '16px', height: '16px', accentColor: 'var(--color-main)' }} />
            <span style={{ fontSize: '13px', color: '#374151' }}>Enviar lembrete automático ao tutor (24h antes)</span>
          </label>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '10px', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: '10px 20px', border: '1.5px solid #e5e7eb', borderRadius: '10px', background: '#fff', color: '#374151', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '10px 24px', border: 'none', borderRadius: '10px', background: 'var(--color-main)', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Salvando...' : 'Agendar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Appointment Detail Modal ──────────────────────────────────────────────────
function AppointmentDetailModal({ isOpen, schedule, onClose, onUpdated, onStart, onView }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const isMobile = useIsMobile();
  const [cancellationReason, setCancellationReason] = useState('');
  const [showReschedule, setShowReschedule] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [returnDays, setReturnDays] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (isOpen && schedule) {
      const dt = dayjs.utc(schedule.dateTime);
      setRescheduleDate(dt.format('YYYY-MM-DD'));
      setRescheduleTime(dt.format('HH:mm'));
      setCancellationReason('');
      setShowCancelForm(false);
      setShowReschedule(false);
      setIsRejecting(false);
      setReturnDays('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, schedule?.id]);

  if (!isOpen || !schedule) return null;

  const isReadOnly = ['completed', 'canceled', 'no_show'].includes(schedule.status);

  const updateStatus = async (status, extra = {}) => {
    setSaving(true);
    const result = await agendaService.updateAgenda(schedule.id, { status, ...extra });
    setSaving(false);
    if (result.success) {
      toast.success('Status atualizado!');
      onUpdated && onUpdated(schedule.id, status);
      onClose();
    } else {
      toast.error(result.error || 'Erro ao atualizar status');
    }
  };

  const handleCancel = async () => {
    const action = isRejecting ? 'rejeição' : 'cancelamento';
    if (!cancellationReason.trim()) return toast.error(`Informe o motivo da ${action}`);
    setSaving(true);
    const newStatus = isRejecting ? 'rejected' : 'canceled';
    const result = await agendaService.updateAgenda(schedule.id, { status: newStatus, cancellationReason, entityNotes: cancellationReason });
    setSaving(false);
    if (result.success) {
      toast.success(`Agendamento ${isRejecting ? 'rejeitado' : 'cancelado'}`);
      onUpdated && onUpdated(schedule.id, newStatus);
      onClose();
    } else {
      toast.error(result.error || 'Erro ao cancelar agendamento');
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) return toast.error('Informe nova data e hora');
    const dateTime = dayjs(`${rescheduleDate}T${rescheduleTime}`);
    if (dateTime.isBefore(dayjs())) return toast.error('Não é possível reagendar para uma data/hora passada');
    const dateTimeStr = dateTime.format('YYYY-MM-DDTHH:mm:ss');
    setSaving(true);
    const result = await agendaService.updateAgenda(schedule.id, { dateTime: dateTimeStr });
    setSaving(false);
    if (result.success) {
      toast.success('Agendamento reagendado!');
      onUpdated && onUpdated(schedule.id, schedule.status);
      setShowReschedule(false);
      onClose();
    } else {
      toast.error(result.error || 'Erro ao reagendar');
    }
  };

  const handleComplete = async () => {
    const extra = returnDays ? { returnSuggestionDays: Number(returnDays) } : {};
    await updateStatus('completed', extra);
  };

  const handleRemove = async () => {
    setSaving(true);
    const result = await agendaService.removeAgenda(schedule.id);
    setSaving(false);
    if (result.success) {
      toast.success('Agendamento removido!');
      onUpdated && onUpdated(schedule.id, 'removed');
      onClose();
    } else {
      toast.error(result.error || 'Erro ao remover agendamento');
    }
  };

  const inputSt = {
    width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb',
    borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box',
    outline: 'none', fontFamily: 'inherit', background: '#fff',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleRemove}
        title="Remover Agendamento"
        message="Tem certeza que deseja remover este agendamento? Todos os documentos vinculados, a consulta associada, medicamentos e documentos da consulta serão removidos permanentemente."
        confirmText="Remover"
        type="danger"
      />
      <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111' }}>Detalhes do Agendamento</h2>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#9ca3af' }}>#{schedule.id}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <StatusBadge status={schedule.status} />
            <button onClick={onClose} style={{ border: 'none', background: '#f3f4f6', cursor: 'pointer', width: '32px', height: '32px', borderRadius: '50%', fontSize: '18px', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Pet */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px', background: '#f9fafb', borderRadius: '12px' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', overflow: 'hidden', background: '#e5e7eb', flexShrink: 0 }}>
              <img src={schedule.pet?.photo || placeholderImage} alt={schedule.pet?.name || 'Pet'}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={e => { e.target.onerror = null; e.target.src = placeholderImage; }} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111' }}>{schedule.pet?.name || 'Pet não informado'}</p>
              {schedule.pet?.species && <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6b7280' }}>{getSpeciesLabel(schedule.pet.species)}</p>}
            </div>
          </div>

          {/* Info grid */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
            <InfoBox label="Tutor">
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#111' }}>{schedule.owner?.name || '—'}</p>
              {schedule.owner?.phone && (
                <p
                  title="Clique para copiar"
                  onClick={() => { navigator.clipboard.writeText(schedule.owner.phone); toast.success('Telefone copiado!'); }}
                  style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280', cursor: 'pointer', display: 'inline-block', padding: '2px 4px', borderRadius: '4px', transition: 'background 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(113,101,255,0.08)'; e.currentTarget.style.color = 'var(--color-main)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
                >
                  {formatPhone(schedule.owner.phone)}
                </p>)}
            </InfoBox>
            <InfoBox label="Data e Hora">
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#111' }}>{fmtDateTime(schedule.dateTime)}</p>
            </InfoBox>
            {schedule.service && (
              <InfoBox label="Serviço">
                <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#111' }}>{schedule.service.name}</p>
              </InfoBox>
            )}
          </div>

          {/* Notes */}
          {schedule.notes && (
            <div style={{ padding: '12px', background: '#fffbeb', borderRadius: '10px', border: '1px solid #fef3c7' }}>
              <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '600', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Observações</p>
              <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>{schedule.notes}</p>
            </div>
          )}

          {/* Cancellation reason */}
          {schedule.status === 'canceled' && schedule.cancellationReason && (
            <div style={{ padding: '12px', background: '#FFF5F5', borderRadius: '10px', border: '1px solid #FEE2E2' }}>
              <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '600', color: '#DC2626', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Motivo do Cancelamento</p>
              <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>{schedule.cancellationReason}</p>
            </div>
          )}

          {/* Reschedule */}
          {!isReadOnly && (
            <div>
              <button onClick={() => setShowReschedule(v => !v)}
                style={{ background: 'none', border: '1.5px solid #e5e7eb', borderRadius: '10px', padding: '8px 14px', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                Reagendar
              </button>
              {showReschedule && (
                <div style={{ marginTop: '10px', padding: '14px', background: '#f9fafb', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Nova Data</label>
                      <input type="date" value={rescheduleDate} min={dayjs().format('YYYY-MM-DD')} onChange={e => setRescheduleDate(e.target.value)} style={inputSt} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>Nova Hora</label>
                      <input type="time" value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)} style={inputSt} />
                    </div>
                  </div>
                  <button onClick={handleReschedule} disabled={saving}
                    style={{ padding: '9px 18px', border: 'none', borderRadius: '10px', background: '#374151', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', alignSelf: 'flex-start' }}>
                    {saving ? 'Salvando...' : 'Confirmar Reagendamento'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Return suggestion — only for in_progress
          {schedule.status === 'in_progress' && (
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>Sugestão de Retorno (ao finalizar)</label>
              <select value={returnDays} onChange={e => setReturnDays(e.target.value)} style={inputSt}>
                {RETURN_DAYS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )} */}

          {/* Cancel form */}
          {showCancelForm && (
            <div style={{ padding: '14px', background: '#fff5f5', borderRadius: '10px', border: '1.5px solid #fee2e2', display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
              <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#DC2626' }}>Motivo da {isRejecting ? 'Rejeição' : 'Cancelamento'} *</p>
              <textarea value={cancellationReason} onChange={e => setCancellationReason(e.target.value)} rows={3}
                placeholder={`Descreva o motivo da ${isRejecting ? 'rejeição' : 'cancelamento'}...`}
                style={{ ...inputSt, resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setShowCancelForm(false)}
                  style={{ padding: '8px 16px', border: '1.5px solid #e5e7eb', borderRadius: '8px', background: '#fff', color: '#374151', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                  Voltar
                </button>
                <button onClick={handleCancel} disabled={saving}
                  style={{ padding: '8px 16px', border: 'none', borderRadius: '8px', background: '#DC2626', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                  {saving ? 'Salvando...' : `Confirmar ${isRejecting ? 'Rejeição' : 'Cancelamento'}`}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!showCancelForm && (
          <div style={{ padding: isMobile ? '12px 16px' : '16px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '8px', flexWrap: 'wrap', flexShrink: 0, alignItems: isMobile ? 'stretch' : 'center', justifyContent: isMobile ? 'flex-start' : 'space-between' }}>
            {/* Primary actions — left side */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              {schedule.status === 'scheduled' && (
                <>
                  <button onClick={() => onStart && onStart(schedule.id)} disabled={saving}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', border: 'none', borderRadius: '10px', background: 'var(--color-main)', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                    Iniciar Atendimento
                  </button>
                </>
              )}
              {schedule.status === 'confirmed' && (
                <button onClick={() => onStart && onStart(schedule.id)} disabled={saving}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', border: 'none', borderRadius: '10px', background: 'var(--color-main)', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                  Iniciar Atendimento
                </button>
              )}
              {schedule.status === 'pending' && (
                <>
                  <button onClick={() => updateStatus('confirmed')} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', border: 'none', borderRadius: '10px', background: '#DCFCE7', color: '#16A34A', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    Confirmar
                  </button>
                  <button onClick={() => { setShowCancelForm(true); setIsRejecting(true); }} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', border: '1.5px solid #FEE2E2', borderRadius: '10px', background: '#FFF5F5', color: '#DC2626', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    Rejeitar
                  </button>
                </>
              )}
              {schedule.status === 'in_progress' && (
                <>
                  <button onClick={() => onView && onView(schedule)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', border: 'none', borderRadius: '10px', background: 'var(--color-main)', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    Ver Atendimento
                  </button>
                  <button onClick={handleComplete} disabled={saving}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', border: 'none', borderRadius: '10px', background: 'rgba(113,101,255,0.1)', color: 'var(--color-main)', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    Finalizar
                  </button>
                </>
              )}
              {schedule.status === 'completed' && (
                <button onClick={() => onView && onView(schedule)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', border: 'none', borderRadius: '10px', background: 'var(--color-main)', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  Ver Atendimento
                </button>
              )}
              {(schedule.status === 'scheduled' || schedule.status === 'confirmed') && (
                <button onClick={() => updateStatus('no_show')} disabled={saving}
                  style={{ padding: '9px 16px', border: '1.5px solid #e5e7eb', borderRadius: '10px', background: '#fff', color: '#6B7280', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                  Não compareceu
                </button>
              )}

            </div>

            {/* Destructive actions — right side */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              {!isReadOnly && (
                <button onClick={() => { setShowCancelForm(true); setIsRejecting(false); }}
                  style={{ padding: '9px 16px', border: '1.5px solid #e5e7eb', borderRadius: '10px', background: '#fff', color: '#6B7280', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                  Cancelar agendamento
                </button>
              )}
              <button onClick={() => setShowDeleteConfirm(true)} disabled={saving}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', border: '1.5px solid #FEE2E2', borderRadius: '10px', background: '#FFF5F5', color: '#DC2626', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                Remover
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Small helper for info boxes in detail modal
function InfoBox({ label, children }) {
  return (
    <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '10px' }}>
      <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</p>
      {children}
    </div>
  );
}

// ── Appointment Card ──────────────────────────────────────────────────────────
function AppointmentCard({ schedule, onViewDetail, onStart, startingId }) {
  const cfg = STATUS_CONFIG[schedule.status] || STATUS_CONFIG.scheduled;
  const isActionable = schedule.status === 'scheduled' || schedule.status === 'confirmed';
  const isInProgress = schedule.status === 'in_progress';

  return (
    <div
      style={{
        background: '#fff',
        border: `1.5px solid ${isInProgress ? '#FEF3C7' : '#f0f0f0'}`,
        borderLeft: `4px solid ${cfg.color}`,
        borderRadius: '12px',
        padding: '14px 16px',
        cursor: 'pointer',
        transition: 'box-shadow 0.15s',
        boxShadow: isInProgress ? '0 2px 12px rgba(217,119,6,0.12)' : '0 1px 4px rgba(0,0,0,0.04)',
      }}
      onClick={() => onViewDetail(schedule)}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = isInProgress ? '0 2px 12px rgba(217,119,6,0.12)' : '0 1px 4px rgba(0,0,0,0.04)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Time */}
        <div style={{ flexShrink: 0, textAlign: 'center', minWidth: '46px' }}>
          <p style={{ margin: 0, fontSize: '15px', fontWeight: '800', color: cfg.color, fontVariantNumeric: 'tabular-nums' }}>
            {fmtTime(schedule.dateTime)}
          </p>
        </div>

        {/* Pet Photo */}
        <div style={{ width: '46px', height: '46px', borderRadius: '50%', overflow: 'hidden', background: '#f3f4f6', flexShrink: 0 }}>
          <img src={schedule.pet?.photo || placeholderImage} alt={schedule.pet?.name || 'Pet'}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { e.target.onerror = null; e.target.src = placeholderImage; }} />
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <p style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111' }}>
              {schedule.pet?.name || 'Pet não informado'}
            </p>
            <StatusBadge status={schedule.status} small />
          </div>
          {schedule.owner && (
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              {schedule.owner.name}
              {schedule.owner.phone && <span style={{ color: '#9ca3af', marginLeft: '4px' }}>{formatPhone(schedule.owner.phone)}</span>}
            </p>
          )}
          {schedule.service && (
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>{schedule.service.name}</p>
          )}
          {schedule.notes && (
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9ca3af', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '340px' }}>
              "{schedule.notes}"
            </p>
          )}
        </div>

        {/* Quick action */}
        <div style={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          {isActionable && (
            <button onClick={() => onStart(schedule.id)} disabled={startingId === schedule.id}
              style={{ padding: '7px 16px', border: 'none', borderRadius: '8px', background: 'var(--color-main)', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: startingId === schedule.id ? 'not-allowed' : 'pointer', opacity: startingId === schedule.id ? 0.7 : 1, whiteSpace: 'nowrap' }}>
              {startingId === schedule.id ? '...' : '▶ Iniciar'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
function Schedules({ onStartConsultation, onViewConsultation }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entity, setEntity] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    const v = searchParams.get('view');
    return VIEW_MODES.some(m => m.value === v) ? v : 'day';
  });
  const [currentDate, setCurrentDate] = useState(() => {
    const d = searchParams.get('date');
    const parsed = d ? dayjs(d, 'YYYY-MM-DD') : null;
    return parsed && parsed.isValid() ? parsed : dayjs();
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [detailSchedule, setDetailSchedule] = useState(null);
  const [startingId, setStartingId] = useState(null);
  const [calMonth, setCalMonth] = useState(dayjs());
  const isMobile = useIsMobile();

  useEffect(() => { loadEntity(); }, []);

  useEffect(() => {
    if (entity) loadAgenda();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.id, viewMode, currentDate]);

  useEffect(() => {
    setSearchParams({ date: currentDate.format('YYYY-MM-DD'), view: viewMode }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, viewMode]);

  const loadEntity = async () => {
    const result = await entityService.getMyEntities();
    if (result.success && result.entities?.length > 0) {
      setEntity(result.entities[0]);
    } else {
      setLoading(false);
    }
  };

  const loadAgenda = async () => {
    if (!entity) return;
    setLoading(true);
    const result = await agendaService.getAgenda({
      entityId: entity.id,
      view: viewMode,
      date: currentDate.format('YYYY-MM-DD'),
    });
    setSchedules(result.success ? (result.schedules || []) : []);
    setLoading(false);
  };

  const navigateDate = (dir) => {
    const unit = viewMode === 'month' ? 'month' : viewMode === 'week' ? 'week' : 'day';
    setCurrentDate(d => dir === 'prev' ? d.subtract(1, unit) : d.add(1, unit));
  };

  const getDateLabel = () => {
    if (viewMode === 'month') return currentDate.format('MMMM [de] YYYY');
    if (viewMode === 'week') {
      const s = currentDate.startOf('week');
      const e = currentDate.endOf('week');
      return `${s.format('DD MMM')} – ${e.format('DD MMM YYYY')}`;
    }
    const isToday = currentDate.isSame(dayjs(), 'day');
    return isToday ? `Hoje, ${currentDate.format('DD [de] MMMM')}` : currentDate.format('ddd, DD [de] MMMM YYYY');
  };

  const handleStart = async (scheduleId) => {
    setStartingId(scheduleId);
    const result = await agendaService.startAppointment(scheduleId);
    setStartingId(null);
    if (result.success) {
      toast.success('Atendimento iniciado!');
      const schedule = schedules.find(s => s.id === scheduleId);
      setDetailSchedule(null);
      loadAgenda();
      if (onStartConsultation && schedule?.pet?.id) {
        onStartConsultation(schedule);
      } else {
        navigate(`/appointment/${scheduleId}`);
      }
    } else {
      toast.error(result.error || 'Erro ao iniciar atendimento');
    }
  };

  const handleUpdated = () => {
    loadAgenda();
  };

  // Filter + sort
  const filtered = schedules
    .filter(s => {
      const okStatus = selectedStatus === 'all' || s.status === selectedStatus;
      const term = searchTerm.toLowerCase();
      const okSearch = !searchTerm || [s.pet?.name, s.owner?.name, s.notes].some(v => v?.toLowerCase().includes(term));
      return okStatus && okSearch;
    })
    .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));

  // Group for week/month views
  const groupedEntries = Object.entries(
    filtered.reduce((acc, s) => {
      const key = dayjs(s.dateTime).format('YYYY-MM-DD');
      if (!acc[key]) acc[key] = [];
      acc[key].push(s);
      return acc;
    }, {})
  ).sort(([a], [b]) => a.localeCompare(b));

  // Stats
  const stats = {
    total: schedules.length,
    confirmed: schedules.filter(s => s.status === 'confirmed').length,
    waiting: schedules.filter(s => s.status === 'scheduled').length,
    inProgress: schedules.filter(s => s.status === 'in_progress').length,
    completed: schedules.filter(s => s.status === 'completed').length,
    upcoming: schedules.filter(s => s.status === 'scheduled' || s.status === 'confirmed').length,
  };

  const STATUS_FILTER_OPTIONS = [
    { value: 'all', label: 'Todos' },
    { value: 'scheduled', label: 'Agendado' },
    { value: 'rejected', label: 'Rejeitado' },
    { value: 'pending', label: 'Pendente' },
    { value: 'confirmed', label: 'Confirmado' },
    { value: 'in_progress', label: 'Em atendimento' },
    { value: 'completed', label: 'Finalizado' },
    { value: 'canceled', label: 'Cancelado' },
    { value: 'no_show', label: 'Não compareceu' },
  ];

  const EmptyState = ({ text = 'Nenhum agendamento encontrado' }) => (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
      <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ marginBottom: '14px', opacity: 0.35 }}>
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
      <p style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: '600', color: '#374151' }}>{text}</p>
      <p style={{ margin: 0, fontSize: '13px' }}>
        {searchTerm ? 'Tente ajustar sua busca' : 'Clique em "Novo Agendamento" para começar'}
      </p>
    </div>
  );

  return (
    <>
      <NewAppointmentModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        entityId={entity?.id}
        entity={entity}
        defaultDate={currentDate.format('YYYY-MM-DD')}
        onSaved={loadAgenda}
      />
      <AppointmentDetailModal
        isOpen={!!detailSchedule}
        schedule={detailSchedule}
        onClose={() => setDetailSchedule(null)}
        onUpdated={handleUpdated}
        onStart={handleStart}
        onView={(schedule) => {
          // Se não tiver pet, vai para a tela de resumo do atendimento
          if (!schedule.pet?.id && schedule.id) {
            navigate(`/appointment/${schedule.id}`);
            return;
          }
          setDetailSchedule(null);
          if (onViewConsultation) {
            onViewConsultation(schedule);
          }
        }}
      />
      <ScheduleSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onSave={() => { setShowSettings(false); loadAgenda(); }}
      />

      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '20px', alignItems: 'flex-start' }}>
      {/* ── Main ── */}
      <div style={{ flex: 1, minWidth: 0 }}>

      {/* ── Header ── */}
      <div className="mr-tutores-header">
        <div>
          <h1 className="mr-tutores-title">Agenda</h1>
          <p className="mr-tutores-subtitle">Gerencie os atendimentos da clínica</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* View mode */}
          <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '10px', padding: '3px', gap: '2px' }}>
            {VIEW_MODES.map(({ value, label }) => (
              <button key={value} onClick={() => setViewMode(value)}
                style={{ padding: '6px 14px', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                  background: viewMode === value ? '#fff' : 'transparent',
                  color: viewMode === value ? 'var(--color-main)' : '#666',
                  boxShadow: viewMode === value ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s' }}>
                {label}
              </button>
            ))}
          </div>
          {/* Settings */}
          <button onClick={() => setShowSettings(true)}
            title="Agenda"
            className="mr-action-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
          </button>
          {/* New appointment */}
          <button onClick={() => setShowNewModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', border: 'none', borderRadius: '10px', background: 'var(--color-main)', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Novo Agendamento
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="mr-tutores-filters">
        {/* Date navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <button className="mr-action-btn" onClick={() => navigateDate('prev')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151', whiteSpace: 'nowrap', textTransform: 'capitalize', minWidth: '160px', textAlign: 'center' }}>
            {getDateLabel()}
          </span>
          <button className="mr-action-btn" onClick={() => navigateDate('next')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button className="mr-action-btn" onClick={() => setCurrentDate(dayjs())} title="Ir para hoje"
            style={{ fontSize: '11px', fontWeight: '700', width: 'auto', padding: '0 10px' }}>
            Hoje
          </button>
        </div>
        {/* Search */}
        <div className="mr-search mr-tutores-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <input type="text" placeholder="Buscar pet ou tutor..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ border: 'none', outline: 'none', fontSize: '14px', color: '#374151', background: 'transparent', width: '100%' }} />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '18px', lineHeight: 1, padding: 0 }}>×</button>
          )}
        </div>
        {/* Status */}
        <div className="mr-filter-group">
          <span className="mr-filter-label">STATUS:</span>
          <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)} className="mr-filter-select-native">
            {STATUS_FILTER_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="shimmer" style={{ height: '80px', borderRadius: '12px' }} />
          ))}
        </div>
      ) : viewMode === 'day' ? (
        /* Day view — flat list */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filtered.length === 0
            ? <EmptyState text="Nenhum agendamento para este dia" />
            : filtered.map(s => (
                <AppointmentCard
                  key={s.id}
                  schedule={s}
                  onViewDetail={setDetailSchedule}
                  onStart={handleStart}
                  startingId={startingId}
                />
              ))
          }
        </div>
      ) : (
        /* Week / Month view — grouped by day */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {groupedEntries.length === 0
            ? <EmptyState text="Nenhum agendamento neste período" />
            : groupedEntries.map(([dateKey, daySchedules]) => {
                const isToday = dayjs(dateKey).isSame(dayjs(), 'day');
                const dayLabel = dayjs(dateKey).format('dddd, DD [de] MMMM');
                return (
                  <div key={dateKey}>
                    {/* Day header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', padding: '10px 16px',
                      background: isToday ? 'rgba(113,101,255,0.08)' : '#f9fafb',
                      borderRadius: '10px', border: `1.5px solid ${isToday ? 'rgba(113,101,255,0.25)' : 'transparent'}`,
                      cursor: 'pointer' }}
                      onClick={() => { setCurrentDate(dayjs(dateKey)); setViewMode('day'); }}>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: isToday ? 'var(--color-main)' : '#374151', textTransform: 'capitalize', flex: 1 }}>
                        {dayLabel}
                        {isToday && <span style={{ marginLeft: '8px', fontSize: '11px', background: 'var(--color-main)', color: '#fff', borderRadius: '6px', padding: '2px 7px' }}>Hoje</span>}
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--color-main)', background: '#fff', borderRadius: '20px', padding: '2px 12px' }}>
                        {daySchedules.length} agend.
                      </span>
                      <span style={{ fontSize: '12px', color: '#9ca3af' }}>Ver dia ›</span>
                    </div>

                    {/* Preview rows */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', paddingLeft: '8px' }}>
                      {daySchedules.slice(0, 5).map(s => {
                        const cfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.scheduled;
                        return (
                          <div key={s.id} onClick={() => setDetailSchedule(s)}
                            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#fff', borderRadius: '8px', borderLeft: `3px solid ${cfg.color}`, cursor: 'pointer', transition: 'background 0.1s' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                            <span style={{ fontSize: '13px', fontWeight: '700', color: cfg.color, minWidth: '46px', fontVariantNumeric: 'tabular-nums' }}>{fmtTime(s.dateTime)}</span>
                            <span style={{ fontSize: '13px', color: '#374151', flex: 1 }}>
                              <strong>{s.pet?.name || '—'}</strong>
                              {s.owner?.name && <span style={{ color: '#9ca3af' }}> · {s.owner.name}</span>}
                            </span>
                            <StatusBadge status={s.status} small />
                          </div>
                        );
                      })}
                      {daySchedules.length > 5 && (
                        <div onClick={() => { setCurrentDate(dayjs(dateKey)); setViewMode('day'); }}
                          style={{ padding: '6px 12px', fontSize: '12px', color: 'var(--color-main)', cursor: 'pointer', fontWeight: '600' }}>
                          + {daySchedules.length - 5} mais agendamentos
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
          }
        </div>
      )}

      </div>{/* end main */}

      {/* ── Sidebar ── */}
      <div style={{ width: isMobile ? '100%' : '256px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '14px', position: isMobile ? 'static' : 'sticky', top: '20px' }}>

        {/* Resumo do Dia */}
        <div style={{ background: 'var(--color-main, #7165FF)', borderRadius: '18px', padding: '22px 20px', color: '#fff' }}>
          <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '600', opacity: 0.8, letterSpacing: '0.3px' }}>
            {{ day: 'Resumo do Dia', week: 'Resumo da Semana', month: 'Resumo do Mês' }[viewMode]}
          </p>
          <p style={{ margin: 0, fontSize: '52px', fontWeight: '800', lineHeight: 1 }}>{stats.total}</p>
          <p style={{ margin: '0 0 22px', fontSize: '13px', fontWeight: '700', opacity: 0.85 }}>Agendamentos Totais</p>
          <div style={{ marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '7px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600' }}>Concluídos</span>
              <span style={{ fontSize: '13px', fontWeight: '800' }}>{String(stats.completed)}</span>
            </div>
            <div style={{ height: '4px', background: 'rgba(255,255,255,0.3)', borderRadius: '2px' }}>
              <div style={{ height: '100%', background: '#fff', borderRadius: '2px', width: stats.total ? `${Math.round((stats.completed / stats.total) * 100)}%` : '0%', transition: 'width 0.4s' }} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.2)', marginBottom: '10px' }}>
            <span style={{ fontSize: '13px', fontWeight: '600' }}>Em Espera</span>
            <span style={{ fontSize: '13px', fontWeight: '800' }}>{String(stats.waiting)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '13px', fontWeight: '600' }}>Confirmados</span>
            <span style={{ fontSize: '13px', fontWeight: '800' }}>{String(stats.confirmed)}</span>
          </div>
        </div>

        {/* Mini Calendar */}
        {(() => {
          const monthStart = calMonth.startOf('month');
          const startDow = monthStart.day();
          const daysInMonth = calMonth.daysInMonth();
          const cells = [];
          for (let i = 0; i < startDow; i++) cells.push(null);
          for (let d = 1; d <= daysInMonth; d++) cells.push(d);
          return (
            <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '18px', padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{ fontSize: '15px', fontWeight: '700', color: '#111', textTransform: 'capitalize' }}>
                  {calMonth.format('MMMM YYYY')}
                </span>
                <div style={{ display: 'flex', gap: '2px' }}>
                  <button onClick={() => setCalMonth(m => m.subtract(1, 'month'))}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', fontSize: '16px', color: '#374151', lineHeight: 1 }}>‹</button>
                  <button onClick={() => setCalMonth(m => m.add(1, 'month'))}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', fontSize: '16px', color: '#374151', lineHeight: 1 }}>›</button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '6px' }}>
                {['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'].map(d => (
                  <span key={d} style={{ textAlign: 'center', fontSize: '10px', fontWeight: '700', color: '#9ca3af', padding: '2px 0' }}>{d}</span>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                {cells.map((day, i) => {
                  if (!day) return <span key={`e${i}`} />;
                  const date = calMonth.date(day);
                  const isToday = date.isSame(dayjs(), 'day');
                  const isSelected = date.isSame(currentDate, 'day');
                  const isActive = isSelected || isToday;
                  return (
                    <button key={day}
                      onClick={() => { setCurrentDate(date); setViewMode('day'); }}
                      style={{
                        border: 'none', borderRadius: '8px', padding: '6px 0', cursor: 'pointer',
                        fontSize: '13px', fontWeight: isActive ? '700' : '500',
                        background: isActive ? 'var(--color-main, #7165FF)' : 'transparent',
                        color: isActive ? '#fff' : '#374151',
                        transition: 'background 0.15s',
                      }}>
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

      </div>{/* end sidebar */}
      </div>{/* end layout */}
    </>
  );
}

export default Schedules;
